class GiftingOptions extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    this.updateGiftOption = this.updateGiftOption.bind(this)
    this.updateInnerOverlay = this.updateInnerOverlay.bind(this)
    this.clearSelections = this.clearSelections.bind(this)
    this.clearCartNote = this.clearCartNote.bind(this)
    this.checkMessageValue = this.checkMessageValue.bind(this)
    this.populateExistingGiftMessage = this.populateExistingGiftMessage.bind(this)
    this.openOptions = this.openOptions.bind(this)
    this.submitGiftMessage = this.submitGiftMessage.bind(this)
    this.markAsAdded = this.markAsAdded.bind(this)
    this.clearGiftOption = this.clearGiftOption.bind(this)
    this.handleCartItemRemoved = this.handleCartItemRemoved.bind(this)

    if (this.externalEls?.editTrigger?.exists) {
      this.listeners.add(this.externalEls.editTrigger.element, 'click', this.openOptions)
    }

    for (const optionEl of this.els.option.elements) {
      this.listeners.add(optionEl, 'click', this.updateGiftOption)
    }

    if (this.externalEls?.innerOverlay?.exists) {
      this.listeners.add(this.externalEls.innerOverlay.element, 'click', this.updateInnerOverlay)
    }

    if (this.externalEls?.accordionTrigger?.exists) {
      for (const accordionEl of this.externalEls.accordionTrigger.elements) {
        this.listeners.add(accordionEl, 'click', this.updateInnerOverlay)
      }
    }

    this.listeners.add(this.els.clear.element, 'click', this.clearGiftOption)
    this.listeners.add(this.els.message.element, 'input', this.checkMessageValue)
    this.listeners.add(this.els.submit.element, 'click', this.submitGiftMessage)
    this.listeners.add(window, 'load', this.populateExistingGiftMessage)

    // Listen for cart item removals to clear note when needed
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
      this.handleCartItemRemoved,
    )
  }

  reInit () {
    this.listeners.removeAll()
    this.setupListeners()
  }

  async clearGiftOption () {
    await this.clearCartNote()
    this.clearSelections()
    this.clearTextarea()
    this.markAsAdded(false)
    this.hideNote()

    // Reload the page if it's the main cart.
    if (this.isMainCart) {
      window.location.reload()
    }
  }

  async submitGiftMessage (event) {
    if (this.isMainCart) return

    event.preventDefault()

    const form = event.target.closest('form')

    await window.BAO.Cart.addItemFromForm(form)

    this.markAsAdded(true)
    this.updateInnerOverlay(false)
    this.reInit()
  }

  async populateExistingGiftMessage () {
    const cart = await window.BAO.Utils.fetchJSON('/cart.json')

    if (!cart.note) {
      return
    }

    this.els.message.element.value = cart.note
    this.markAsAdded(true)
  }

  openOptions () {
    // create a pseudo click event to open the accordion
    this.externalEls.accordionTrigger.element.click()

    this.setScrollOnDrawer(true)
  }

  setScrollOnDrawer (hide = false) {
    if (!this.externalEls?.drawer?.exists) return

    this.externalEls.drawer.element.style.overflowY = hide ? 'hidden' : 'auto'
  }

  updateGiftOption (event) {
    const value = event.target.value

    this.els.hiddenGiftOption.element.value = value
    this.els.hiddenGiftOptionQuantity.element.value = 1

    this.updateClearButton()
  }

  checkMessageValue (event) {
    if (this.els.message.element.value.length > 0) {
      this.els.clear.element.setAttribute('aria-hidden', 'false')
    } else {
      this.els.clear.element.setAttribute('aria-hidden', 'true')
    }

    this.updateHiddenGiftMessage(event)
  }

  updateHiddenGiftMessage (event) {
    if (!this.els.hiddenCartMessage?.element?.exists) return

    this.els.hiddenCartMessage.element.value = event.target.value
  }

  updateClearButton () {
    const anyOptionSelected = this.els.option.elements.some(optionEl => optionEl.checked)

    this.els.clear.element.setAttribute('aria-hidden', anyOptionSelected ? 'false' : 'true')
  }

  async clearCartNote () {
    const cart = await window.BAO.Utils.fetchJSON('/cart.json')
    if (!cart.note) return

    cart.note = ''

    await window.BAO.Utils.fetchJSON('/cart/update.json', {
      method: 'POST',
      body: JSON.stringify(cart),
    })

    this.hideNote()
  }

  hideNote () {
    this.externalEls.note.element.style.display = 'none'
    this.clearTextarea()
  }

  clearSelections () {
    for (const optionEl of this.els.option.elements) {
      optionEl.checked = false
    }

    this.els.hiddenGiftOption.element.value = ''
    this.els.clear.element.setAttribute('aria-hidden', 'true')

    this.els.hiddenGiftOptionQuantity.element.value = 0
  }

  clearTextarea () {
    this.els.message.element.value = ''
  }

  updateInnerOverlay (event) {
    const innerOverlay = this.externalEls?.innerOverlay?.element
    if (!innerOverlay) return

    let isHidden = true

    const target = event && event.target
    const accordion = target && typeof target.closest === 'function'
      ? target.closest('[data-gifting-options-external-el="accordion"]')
      : null
    if (accordion) {
      isHidden = accordion.getAttribute('aria-expanded') === 'true'
    } else {
      isHidden = innerOverlay.getAttribute('aria-hidden') === 'true'
    }

    innerOverlay.setAttribute('aria-hidden', isHidden ? 'false' : 'true')

    this.setScrollOnDrawer(isHidden)

    if (event === false || event.target === innerOverlay) {
      this.updateActiveAccordions()
    }
  }

  updateActiveAccordions () {
    const activeAccordions = this.externalEls.accordionTrigger.elements.filter(accordion => {
      const parent = accordion.closest('[data-accordion-group-el="item"]')
      return parent.getAttribute('aria-expanded') === 'true'
    })

    for (const accordion of activeAccordions) {
      const parent = accordion.closest('[data-accordion-group-el="item"]')
      parent.setAttribute('aria-expanded', 'false')
    }
  }

  markAsAdded (add = true) {
    this.externalEls.accordionTrigger.element.setAttribute('data-has-been-added', add ? 'true' : 'false')
  }

  async handleCartItemRemoved () {
    try {
      const cart = await window.BAO.Utils.fetchJSON('/cart.json')

      if (cart.items.length === 0) {
        await this.clearCartNote()
      }
    } catch (error) {
      console.error('Error handling cart change:', error)
    }
  }

  get isMainCart () {
    return this.hasAttribute('data-main-cart')
  }
}

customElements.define('gifting-options', GiftingOptions)
